import styled, { createGlobalStyle, css } from 'styled-components';
import {
  Box,
  isDarkThemeMixin,
  isMobileStyleMixin,
  isNotDarkThemeMixin,
  Link,
  styleMixinFocusOutline,
  styleMixinInvertedFocusOutline,
} from '@amzn/storm-ui';
import { ChromeThemeStyles } from 'src/commons/components/ChromeApp/ChromeAppUtils';
import { MOBILE_MENU_Z_INDEX } from 'src/commons/constants';

const marginStartMixin = css<{ shouldShiftAppWrapper: boolean }>`
  ${(props) =>
    props.shouldShiftAppWrapper ? 'margin-inline-start: 64px;' : ''}
`;
export const GlobalStyle = createGlobalStyle<{
  shouldShiftAppWrapper: boolean;
  isFromRodeo?: boolean;
  theme: ChromeThemeStyles;
  isInterstitialPage?: boolean;
  isMobile?: boolean;
}>`
  html {
    height: 100vh;
    display: flex;
    flex-direction: column;
  }

  body {
    flex: 1;
    display: flex;
    flex-direction: column;
    font-family: "Amazon Ember", Arial, Helvetica, sans-serif;
    margin: 0;
    padding: 0;
    ${({ theme, isInterstitialPage = false, isMobile = false }) =>
      isInterstitialPage &&
      css`
        background-color: ${isMobile
          ? theme.color.gray[75]
          : theme.color.gray[50]};
      `}
  }

  .chrome-wrapper {
    display: flex;
    flex-direction: column;
  }

  .app-and-pivot-wrapper {
    flex: 1;
    display: flex;
    flex-direction: ${({ shouldShiftAppWrapper }) =>
      shouldShiftAppWrapper ? 'row' : 'column'};
    ${marginStartMixin}
    transition: margin-inline-start 0.15s ease;
  }

  #PivotContainer {
    width: auto;
    background-color: transparent;
    ${({ shouldShiftAppWrapper }) =>
      shouldShiftAppWrapper
        ? css`
            height: 100%;
          `
        : css``};
  }

  #ChromeHeaderContainer {
    position: relative;
  }

  #MainMenuContainer {
    min-height: auto;
  }

  .app-body-wrapper {
    flex: 1;
    overflow: auto;
    ${({ isFromRodeo }) => (isFromRodeo ? 'padding: 14px 18px 18px;' : '')}
  }

  #app-content {
    flex: 1;
    overflow: auto;
    ${({ isFromRodeo }) => (isFromRodeo ? 'padding: 14px 18px 18px;' : '')}
  }

  #a-page {
    display: flex;
    flex-direction: column;
    flex: 1;
  }

  #sc-content-container {
    flex: 1;
  }

  .pivot-and-app-wrapper {
    flex: 1;
  }

  .sc-content-inner-container {
    display: flex;
    flex-direction: column;
    height: 100%;
  }
`;

export const MenuShiftWrapper = styled.div<{
  shouldShiftAppWrapper: boolean;
}>`
  ${marginStartMixin}
`;

export const HeaderContainer = styled.div`
  position: fixed;
  width: 100%;
  z-index: 399; // header should have most precedence
  border-block-end: 1px solid
    ${({ theme }) => theme.elevation.borderColor.subtle};
  ${isNotDarkThemeMixin(css`
    box-shadow: ${({ theme }) => theme.elevation.shadow.card};
  `)};
`;

export const MarketplaceSwitcherWrapper = styled(Box).attrs({
  padding: 'none',
})<{ isFromTrim?: boolean }>`
  border-radius: 0;

  :not(:empty) {
    padding: ${({ theme }) =>
      `${theme.spacing.base.default} ${theme.spacing.base.default} ${theme.spacing.xxsmall.default}`};

    ${isMobileStyleMixin(css`
      padding: ${({ theme }) =>
        `${theme.spacing.small.mobile} ${theme.spacing.small.mobile} ${theme.spacing.xxsmall.mobile} `};
    `)};

    ${isDarkThemeMixin(css`
      background-color: ${({ theme }) => theme.surface.offset};
    `)};
  }

  ${({ theme, isFromTrim }) =>
    isFromTrim &&
    css`
      box-shadow: ${theme.elevation.shadow.card};
    `};
`;

export const RegionSwitcherWrapper = styled(MarketplaceSwitcherWrapper)``;

export const LogoWrapper = styled(Link)`
  width: 120px;
  height: 30px;
  margin: ${({ theme }) => theme.spacing.xxxsmall.default}
    ${({ theme }) => theme.spacing.small.default} 0
    ${({ theme }) => theme.spacing.large.default};
  flex-shrink: 0;
  border-radius: ${({ theme }) => theme.border.radius.medium};

  img {
    width: 100%;
    height: 100%;
  }

  :focus {
    outline: none;
  }

  :focus-visible {
    ${styleMixinInvertedFocusOutline};

    ${isDarkThemeMixin(css`
      ${styleMixinFocusOutline};
    `)};
  }
`;

export const MobileHeaderContainer = styled.div`
  display: block;
  position: fixed;
  top: 0;
  width: 100%;
  background-color: ${({ theme }) => theme.header.contextBar.backgroundColor};
  z-index: ${MOBILE_MENU_Z_INDEX};
`;

export const StickyMobileHeader = styled.div`
  position: fixed;
  width: 100%;
  z-index: ${MOBILE_MENU_Z_INDEX};
`;

export const EmptyContextBar = styled.div`
  flex-grow: 1;
`;
